import { useState, useRef, useEffect, type ReactNode } from 'react';
import { Icon } from './Icon';

/**
 * Micro tooltip educativo. Renderizza un piccolo cerchio "?" cliccabile
 * che apre un popover con la spiegazione. Auto-close su click fuori o Esc.
 *
 * Uso:
 *   <Helper text="Il SDI è un codice di 7 caratteri usato per la fatturazione elettronica B2B." />
 *
 * Per contenuto ricco (link, list…):
 *   <Helper>
 *     <strong>Codice SDI</strong>
 *     <p>7 caratteri per la fatturazione B2B…</p>
 *   </Helper>
 *
 * Per un position custom (es. tooltip sopra invece di sotto):
 *   <Helper text="…" placement="top" />
 */
export function Helper({
  text,
  children,
  placement = 'bottom',
  size = 12,
}: {
  text?: string;
  children?: ReactNode;
  placement?: 'top' | 'bottom' | 'right' | 'left';
  size?: number;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pos = (() => {
    switch (placement) {
      case 'top':    return { bottom: '100%', left: '50%', transform: 'translateX(-50%)', marginBottom: 8 };
      case 'right':  return { left: '100%', top: '50%', transform: 'translateY(-50%)', marginLeft: 8 };
      case 'left':   return { right: '100%', top: '50%', transform: 'translateY(-50%)', marginRight: 8 };
      case 'bottom':
      default:       return { top: '100%', left: 0, marginTop: 8 };
    }
  })();

  return (
    <span ref={ref} className="inline-flex relative align-middle ml-1.5" style={{ verticalAlign: 'middle' }}>
      <button
        type="button"
        aria-label="Apri spiegazione"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((v) => !v); }}
        /* `hlp-dot` non porta disegno: serve a mobile.css per allargare il
           bersaglio a 44px senza gonfiare il cerchietto, che resta 16. */
        className="hlp-dot inline-grid place-items-center rounded-full transition-colors"
        style={{
          width: size + 4,
          height: size + 4,
          background: open ? 'var(--ink)' : 'var(--paper-2)',
          color: open ? 'var(--paper)' : 'var(--mute-2)',
          border: '1px solid var(--line)',
          fontSize: size - 2,
          fontWeight: 700,
          cursor: 'help',
          lineHeight: 1,
        }}
      >
        ?
      </button>

      {open && (
        <span
          role="tooltip"
          className="absolute z-[1000] shadow-lg text-[13px] leading-[1.5]"
          style={{
            ...pos,
            borderRadius: 'var(--r-ctl)',
            background: 'var(--ink)',
            color: 'var(--paper)',
            padding: '10px 12px',
            minWidth: 220,
            maxWidth: 320,
            border: '1px solid var(--ink)',
            fontFamily: 'inherit',
          }}
        >
          {children ?? text}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            className="absolute -top-1.5 -right-1.5 grid place-items-center rounded-full"
            style={{ width: 16, height: 16, background: 'var(--paper)', color: 'var(--ink)', border: '1px solid var(--line)' }}
          >
            <Icon name="x" size={8} />
          </button>
        </span>
      )}
    </span>
  );
}
